import { faArrowDownArrowUp } from '@fortawesome/pro-regular-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  Currency,
  FiatCurrency,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM, LabelM } from '@sorare/core/src/atoms/typography';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import type { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';

const CurrencyContainer = styled(SBHorizontal)`
  gap: var(--double-unit);
  padding: var(--double-unit);
  background-color: var(--c-nd-50);
  border-radius: var(--double-unit);
`;

const TradeIconContainer = styled(Horizontal).attrs({ gap: 0, center: true })`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  border-radius: 50%;
  background: var(--c-white);
  align-self: center;
  margin: calc(-1 * var(--intermediate-unit)) 0;
`;

const CurrenciesContainer = styled(Vertical)`
  width: 100%;
`;

export const MonetaryRewardConversion = ({
  monetaryAmount,
}: {
  monetaryAmount: MonetaryAmount;
}) => {
  const { fiatCurrency } = useCurrentUserCurrencies();
  const { formatNumber } = useIntlContext();
  const formattedUSD = formatNumber(monetaryAmount.usd, {
    style: 'currency',
    currency: FiatCurrency.USD,
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 0,
  });

  const { main } = useAmountWithConversion({
    monetaryAmount,
    primaryCurrency: Currency.FIAT,
  });

  if (fiatCurrency === FiatCurrency.USD) {
    return null;
  }

  return (
    <CurrenciesContainer gap={2}>
      <BodyM color="var(--c-nd-600)">
        <FormattedMessage
          id="RewardUnlockedDialog.monetaryRewardConversion"
          defaultMessage="Converting to local currency:"
        />
      </BodyM>
      <CurrenciesContainer gap={0}>
        <CurrencyContainer>
          <LabelM color="var(--c-nd-600)">{FiatCurrency.USD}</LabelM>{' '}
          <LabelM color="var(--c-nd-600)">{formattedUSD}</LabelM>
        </CurrencyContainer>
        <TradeIconContainer>
          <FontAwesomeIcon color="var(--c-black)" icon={faArrowDownArrowUp} />
        </TradeIconContainer>
        <CurrencyContainer>
          <LabelM>{fiatCurrency}</LabelM> <LabelM bold>≈ {main}</LabelM>
        </CurrencyContainer>
      </CurrenciesContainer>
    </CurrenciesContainer>
  );
};
